/**
 * Magazzino — «cosa manca e cosa avanza?», per articolo.
 *
 * Riscritta due volte il 16-08. La prima versione affiancava due tabelle e
 * ordinava per grandezza del buco; Enrico: *«mi dà la sensazione che sia meno
 * efficace, anche se non saprei dirti perché»*. La diagnosi: era l'unica delle
 * tre sezioni **che non si poteva usare** — il Cruscotto è fatto di porte, il
 * Portafoglio di filtri, il Magazzino di niente.
 *
 * Cosa è cambiato, in ordine di quanto pesa:
 *  1. **Ordine per lavoro sbloccato**, non per grandezza del buco. Riordinando
 *     salta fuori che 1.441 pallet mancanti fermano 45 commesse, mentre 59.360
 *     pezzi di un altro codice ne fermano due.
 *  2. **«Sblocca» dice chi**, coi nomi dei clienti in attesa: un codice articolo
 *     non dice niente a nessuno.
 *  3. **Una tabella sola coi riquadri che la filtrano**, come nel Portafoglio:
 *     chi arriva riconosce un modello già imparato.
 *  4. **Ogni riga è una porta** verso il Portafoglio filtrato su quell'articolo.
 *  5. **La spunta del materiale di consumo**, che chiude un buco vero: i pallet
 *     EPAL erano l'articolo che bloccava più commesse dell'azienda.
 */
import { Ban, PackageX, RotateCcw, Warehouse } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import { RigaTesta } from "@/components/RigaTesta";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useM1ArticoliCritici,
  useM1ArticoliEsclusi,
  useM1ImpostaEsclusione,
  useM1StockEccedente,
} from "@/hooks/useM1";
import { cn } from "@/lib/utils";
import type { M1DashboardKPIs, M1SegnaleArticoloCritico } from "@/types/api";

const fmt = (v: string | number | null | undefined) =>
  Number(v ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 0, useGrouping: true });

const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function dataBreve(iso: string | null): { testo: string; scaduta: boolean } {
  if (!iso) return { testo: "—", scaduta: false };
  const [a, m, g] = iso.split("-").map(Number);
  const d = new Date(a, m - 1, g);
  return {
    testo: `${g} ${MESI[m - 1]}`,
    scaduta: d.getTime() < Date.now(),
  };
}

type Filtro = "mancano" | "senza_piano" | "in_produzione" | "avanzano" | "esclusi";

/** I valori ammessi nell'URL: uno sconosciuto ricade sul default invece di
 *  mostrare una lista vuota senza spiegazione. */
const FILTRI: Filtro[] = ["mancano", "senza_piano", "in_produzione", "avanzano", "esclusi"];

export function MagazzinoM1({ dashboard }: { dashboard?: M1DashboardKPIs }) {
  const navigate = useNavigate();
  const { data: critici, isLoading: lc } = useM1ArticoliCritici();
  const { data: eccesso, isLoading: le } = useM1StockEccedente();
  const { data: esclusi = [] } = useM1ArticoliEsclusi();
  const impostaEsclusione = useM1ImpostaEsclusione();

  // ⚠️ I filtri vivono nell'**URL**, non in `useState`.
  //
  // Era il difetto corretto nel Portafoglio il 16-08 («l'URL è l'unica fonte») e
  // rimasto qui: la porta «Articoli senza piano» del Cruscotto mostrava 250 e
  // atterrava su una lista di 315, perché arrivando a componente già montato lo
  // stato iniziale non veniva riletto. E nessun filtro del Magazzino era un
  // indirizzo condivisibile.
  const [searchParams, setSearchParams] = useSearchParams();
  const filtroDaUrl = searchParams.get("magazzino") as Filtro | null;
  const filtro: Filtro = FILTRI.includes(filtroDaUrl as Filtro)
    ? (filtroDaUrl as Filtro)
    : "mancano";

  function setFiltro(f: Filtro) {
    const p = new URLSearchParams(searchParams);
    if (f === "mancano") p.delete("magazzino");
    else p.set("magazzino", f);
    setSearchParams(p, { replace: true });
  }
  const [cerca, setCerca] = useState("");
  const [daEscludere, setDaEscludere] = useState<M1SegnaleArticoloCritico | null>(null);
  const [motivo, setMotivo] = useState("");
  /** Ultimo articolo escluso: l'avviso che dice dove è finito e come disfare. */
  const [appenaEscluso, setAppenaEscluso] = useState<{ codice: string; nome: string } | null>(null);

  const senzaPiano = useMemo(
    () => (critici ?? []).filter((a) => Number(a.piano_pronto_in_tempo) === 0),
    [critici]
  );
  const inProduzione = useMemo(
    () => (critici ?? []).filter((a) => Number(a.piano_pronto_in_tempo) > 0),
    [critici]
  );
  // Il residuo utile del vecchio «stock eccedente»: giacenza a fronte di zero ordini.
  const avanzano = useMemo(
    () => (eccesso ?? []).filter((a) => Number(a.domanda_aperta) === 0),
    [eccesso]
  );

  const gapTotale = (critici ?? []).reduce((acc, a) => acc + Number(a.gap), 0);
  const pezziFermi = avanzano.reduce((acc, a) => acc + Number(a.giacenza_disponibile), 0);
  const prontaNonRitirata = Number(dashboard?.dove_la_merce?.magazzino ?? 0);

  const filtra = <T extends { codice_articolo: string; descrizione?: string | null }>(l: T[]) => {
    const q = cerca.trim().toLowerCase();
    if (!q) return l;
    return l.filter(
      (a) =>
        a.codice_articolo.toLowerCase().includes(q) ||
        (a.descrizione ?? "").toLowerCase().includes(q)
    );
  };

  const listaCritici = filtra(
    filtro === "senza_piano" ? senzaPiano : filtro === "in_produzione" ? inProduzione : (critici ?? [])
  );

  /** Le commesse che aspettano quell'articolo: la ricerca del Portafoglio guarda dentro il codice. */
  const apriCommesse = (codice: string) =>
    navigate(`/moduli/order-readiness?tab=portafoglio&q=${encodeURIComponent(codice)}`);

  const contesto =
    filtro === "avanzano"
      ? `${avanzano.length} articoli senza ordini aperti`
      : filtro === "esclusi"
        ? `${esclusi.length} articoli esclusi dal conteggio`
        : `${listaCritici.length} articoli`;

  return (
    <div className="space-y-3">
      <RigaTesta
        contesto={contesto}
        caricatoIl={dashboard?.snapshot_caricato_il}
        destra={
          filtro === "avanzano" || filtro === "esclusi"
            ? "ordinati per giacenza"
            : "ordinati per commesse sbloccate"
        }
      />

      <section className="grid grid-cols-2 gap-2 lg:grid-cols-5">
        <RiquadroFiltro
          label="Che mancano"
          valore={critici?.length}
          sotto={`${fmt(gapTotale)} pezzi di gap`}
          tono="alert"
          attivo={filtro === "mancano"}
          onClick={() => setFiltro("mancano")}
          loading={lc}
        />
        <RiquadroFiltro
          label="Senza nessun piano"
          valore={senzaPiano.length}
          sotto={critici?.length ? `${Math.round((100 * senzaPiano.length) / critici.length)}% dei critici` : "—"}
          tono="alert"
          attivo={filtro === "senza_piano"}
          onClick={() => setFiltro("senza_piano")}
          loading={lc}
        />
        <RiquadroFiltro
          label="Già in produzione"
          valore={inProduzione.length}
          sotto="il piano li copre"
          attivo={filtro === "in_produzione"}
          onClick={() => setFiltro("in_produzione")}
          loading={lc}
        />
        <RiquadroFiltro
          label="Che avanzano"
          valore={avanzano.length}
          sotto={`${fmt(pezziFermi)} pezzi senza ordini`}
          tono="warn"
          attivo={filtro === "avanzano"}
          onClick={() => setFiltro("avanzano")}
          loading={le}
        />
        <RiquadroFiltro
          label="Esclusi"
          valore={esclusi.length}
          sotto={esclusi.length ? "materiale di consumo" : "nessuno marcato"}
          attivo={filtro === "esclusi"}
          onClick={() => setFiltro("esclusi")}
        />
      </section>

      {appenaEscluso && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-brand-600/40 bg-brand-50/60 px-3 py-2 text-[12.5px] dark:bg-brand-950/40">
          <span>
            <b>{appenaEscluso.nome}</b> non blocca più l&apos;evadibilità delle commesse.
          </span>
          <button
            type="button"
            onClick={() => setFiltro("esclusi")}
            className="font-medium text-brand-700 underline-offset-2 hover:underline dark:text-brand-400"
          >
            Lo trovi in «Esclusi»
          </button>
          <button
            type="button"
            disabled={impostaEsclusione.isPending}
            onClick={() => {
              impostaEsclusione.mutate(
                { codice: appenaEscluso.codice, escluso_da_evadibilita: false },
                { onSuccess: () => setAppenaEscluso(null) }
              );
            }}
            className="ml-auto inline-flex items-center gap-1 rounded border bg-card px-2 py-1 text-[11px] hover:bg-accent disabled:opacity-50"
          >
            <RotateCcw className="size-3" />
            Annulla
          </button>
        </div>
      )}

      <input
        type="text"
        value={cerca}
        onChange={(e) => setCerca(e.target.value)}
        placeholder="Cerca codice o descrizione articolo…"
        className="h-9 w-full rounded-md border bg-card px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      />

      <div className="rounded-lg border bg-card">
        {(filtro === "avanzano" ? le : filtro === "esclusi" ? false : lc) ? (
          <Skeleton className="h-64 w-full" />
        ) : filtro === "avanzano" ? (
          <TabellaAvanzano righe={filtra(avanzano)} onApri={apriCommesse} />
        ) : filtro === "esclusi" ? (
          <TabellaEsclusi
            righe={esclusi}
            onRipristina={(codice) => {
              impostaEsclusione.mutate({ codice, escluso_da_evadibilita: false });
              setAppenaEscluso(null);
            }}
            pending={impostaEsclusione.isPending}
          />
        ) : (
          <TabellaMancano
            righe={listaCritici}
            onApri={apriCommesse}
            onEscludi={(a) => {
              setDaEscludere(a);
              setMotivo("");
            }}
          />
        )}
      </div>

      <p className="text-[11px] text-muted-foreground">
        {filtro === "avanzano" ? (
          <>
            Sono <b className="text-foreground">{fmt(pezziFermi)} pezzi</b> in tutto: il magazzino
            gonfio non è qui, sono i <b className="text-foreground">{fmt(prontaNonRitirata)} pezzi</b>{" "}
            già venduti che i clienti non ritirano.
          </>
        ) : filtro === "esclusi" ? (
          <>
            Materiali di servizio e documenti: non sono prodotto finito, quindi non bloccano
            l&apos;evadibilità di una commessa. La stessa marcatura è già rispettata dalla
            Pianificazione.
          </>
        ) : (
          <>
            Ogni riga porta al Portafoglio, filtrato sulle commesse che aspettano quell&apos;articolo.{" "}
            <b className="text-foreground">
              {senzaPiano.length} articoli su {critici?.length ?? 0}
            </b>{" "}
            non hanno nessuna produzione pianificata.
          </>
        )}
      </p>

      <DialogEsclusione
        articolo={daEscludere}
        motivo={motivo}
        setMotivo={setMotivo}
        pending={impostaEsclusione.isPending}
        onClose={() => setDaEscludere(null)}
        onConferma={() => {
          if (!daEscludere) return;
          const nome = daEscludere.descrizione ?? daEscludere.codice_articolo;
          impostaEsclusione.mutate(
            {
              codice: daEscludere.codice_articolo,
              escluso_da_evadibilita: true,
              motivo_esclusione: motivo.trim() || null,
            },
            {
              onSuccess: () => {
                setAppenaEscluso({ codice: daEscludere.codice_articolo, nome });
                setDaEscludere(null);
              },
            }
          );
        }}
      />
    </div>
  );
}

function RiquadroFiltro({
  label,
  valore,
  sotto,
  tono,
  attivo,
  onClick,
  loading,
}: {
  label: string;
  valore: number | undefined;
  sotto: string;
  tono?: "alert" | "warn";
  attivo: boolean;
  onClick: () => void;
  loading?: boolean;
}) {
  const colore =
    tono === "alert"
      ? "text-red-700 dark:text-red-400"
      : tono === "warn"
        ? "text-amber-700 dark:text-amber-400"
        : valore === 0
          ? "text-muted-foreground"
          : "text-foreground";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={attivo}
      className={cn(
        "rounded-lg border bg-card p-2.5 text-left transition-colors",
        attivo
          ? "border-brand-600 bg-brand-50/60 ring-1 ring-inset ring-brand-600 dark:bg-brand-950/40"
          : "hover:bg-accent/40"
      )}
    >
      <span className="block text-[11px] text-muted-foreground">{label}</span>
      {loading ? (
        <Skeleton className="my-1 h-6 w-10" />
      ) : (
        <span className={cn("block text-2xl font-semibold leading-tight tabular-nums", colore)}>
          {fmt(valore ?? 0)}
        </span>
      )}
      <span className="block text-[10px] text-muted-foreground">{sotto}</span>
    </button>
  );
}

const MOSTRATE = 25;

function TabellaMancano({
  righe,
  onApri,
  onEscludi,
}: {
  righe: M1SegnaleArticoloCritico[];
  onApri: (codice: string) => void;
  onEscludi: (a: M1SegnaleArticoloCritico) => void;
}) {
  if (!righe.length) {
    return <p className="p-6 text-center text-sm text-muted-foreground">Nessun articolo.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12.5px]">
        <thead className="bg-muted/40 text-[10.5px] text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Articolo</th>
            <th className="px-3 py-2 text-left font-medium">Sblocca ↓</th>
            <th className="px-3 py-2 text-right font-medium">Manca</th>
            <th className="px-3 py-2 text-right font-medium">Giacenza</th>
            <th className="px-3 py-2 text-left font-medium">Produzione</th>
            <th className="px-3 py-2 text-left font-medium">Prima consegna</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody className="divide-y">
          {righe.slice(0, MOSTRATE).map((a) => {
            const quando = dataBreve(a.prima_data_consegna);
            const altri = a.n_clienti_in_attesa - a.clienti_in_attesa.length;
            return (
              <tr
                key={a.codice_articolo}
                className="cursor-pointer hover:bg-brand-50/50 dark:hover:bg-brand-950/30"
                onClick={() => onApri(a.codice_articolo)}
                title="Apre le commesse che aspettano questo articolo"
              >
                <td className="px-3 py-2">
                  <div className="font-mono text-[11.5px] font-semibold">{a.codice_articolo}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {a.descrizione ?? "senza descrizione in anagrafica"}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="text-[15px] font-semibold tabular-nums">
                    {a.n_ordini_impattati}{" "}
                    <span className="text-[10.5px] font-normal text-muted-foreground">commesse</span>
                  </div>
                  <div className="text-[10.5px] text-muted-foreground">
                    {a.clienti_in_attesa.join(", ")}
                    {altri > 0 && ` +${altri}`}
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  <div className="font-semibold text-red-700 dark:text-red-400">{fmt(a.gap)}</div>
                  <div className="text-[9.5px] text-muted-foreground">
                    su {fmt(a.domanda_aperta)} richiesti
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {fmt(a.giacenza_disponibile)}
                  <div className="text-[9.5px] text-muted-foreground">a magazzino</div>
                </td>
                <td className="px-3 py-2">
                  {Number(a.piano_pronto_in_tempo) > 0 ? (
                    <span className="whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                      in piano {fmt(a.piano_pronto_in_tempo)}
                    </span>
                  ) : (
                    <span className="whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-950 dark:text-red-300">
                      nessun piano
                    </span>
                  )}
                </td>
                <td
                  className={cn(
                    "px-3 py-2 whitespace-nowrap tabular-nums text-[11.5px]",
                    quando.scaduta ? "text-red-700 dark:text-red-400" : "text-muted-foreground"
                  )}
                >
                  {quando.testo}
                </td>
                <td className="px-1 py-2" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => onEscludi(a)}
                    title="Non è prodotto finito: escludilo dal conteggio"
                    className="rounded p-1 text-muted-foreground/60 hover:bg-accent hover:text-foreground"
                  >
                    <Ban className="size-3.5" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {righe.length > MOSTRATE && (
        <p className="border-t px-3 py-2 text-[11px] text-muted-foreground">
          Mostrati i {MOSTRATE} che sbloccano più commesse, su {righe.length}. Per gli altri usa la
          ricerca.
        </p>
      )}
    </div>
  );
}

function TabellaAvanzano({
  righe,
  onApri,
}: {
  righe: { codice_articolo: string; descrizione?: string | null; giacenza_disponibile: string }[];
  onApri: (codice: string) => void;
}) {
  if (!righe.length) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        Nessun articolo a magazzino senza domanda.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12.5px]">
        <thead className="bg-muted/40 text-[10.5px] text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Articolo</th>
            <th className="px-3 py-2 text-right font-medium">Giacenza</th>
            <th className="px-3 py-2 text-left font-medium">Domanda</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {[...righe]
            .sort((a, b) => Number(b.giacenza_disponibile) - Number(a.giacenza_disponibile))
            .slice(0, MOSTRATE)
            .map((a) => (
              <tr
                key={a.codice_articolo}
                className="cursor-pointer hover:bg-accent/30"
                onClick={() => onApri(a.codice_articolo)}
              >
                <td className="px-3 py-2">
                  <div className="font-mono text-[11.5px] font-semibold">{a.codice_articolo}</div>
                  <div className="text-[10px] text-muted-foreground">{a.descrizione ?? "—"}</div>
                </td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">
                  {fmt(a.giacenza_disponibile)}
                  <div className="text-[9.5px] font-normal text-muted-foreground">a magazzino</div>
                </td>
                <td className="px-3 py-2 text-[11px] text-muted-foreground">nessun ordine aperto</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function TabellaEsclusi({
  righe,
  onRipristina,
  pending,
}: {
  righe: { codice: string; descrizione: string | null; motivo_esclusione: string | null }[];
  onRipristina: (codice: string) => void;
  pending: boolean;
}) {
  if (!righe.length) {
    return (
      <div className="p-6 text-center">
        <Warehouse className="mx-auto mb-2 size-5 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Nessun articolo marcato.</p>
        <p className="mx-auto mt-1 max-w-lg text-xs text-muted-foreground">
          Da «Che mancano», il pulsante a fine riga marca un articolo che non è prodotto finito —
          imballaggi, certificati, materiali di consumo — così smette di bloccare l&apos;evadibilità
          delle commesse.
        </p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12.5px]">
        <thead className="bg-muted/40 text-[10.5px] text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Articolo</th>
            <th className="px-3 py-2 text-left font-medium">Motivo</th>
            <th className="w-24" />
          </tr>
        </thead>
        <tbody className="divide-y">
          {righe.map((a) => (
            <tr key={a.codice}>
              <td className="px-3 py-2">
                <div className="font-mono text-[11.5px] font-semibold">{a.codice}</div>
                <div className="text-[10px] text-muted-foreground">{a.descrizione ?? "—"}</div>
              </td>
              <td className="px-3 py-2 text-[11.5px] text-muted-foreground">
                {a.motivo_esclusione ?? "—"}
              </td>
              <td className="px-3 py-2 text-right">
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => onRipristina(a.codice)}
                  className="inline-flex items-center gap-1 rounded border px-2 py-1 text-[11px] text-muted-foreground hover:bg-accent disabled:opacity-50"
                >
                  <RotateCcw className="size-3" />
                  Rimetti
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DialogEsclusione({
  articolo,
  motivo,
  setMotivo,
  pending,
  onClose,
  onConferma,
}: {
  articolo: M1SegnaleArticoloCritico | null;
  motivo: string;
  setMotivo: (v: string) => void;
  pending: boolean;
  onClose: () => void;
  onConferma: () => void;
}) {
  return (
    <Dialog open={!!articolo} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <PackageX className="size-4" />
            Escludi dal conteggio
          </DialogTitle>
          <DialogDescription className="text-xs">
            {articolo?.descrizione ?? articolo?.codice_articolo} non è prodotto finito. Escludendolo
            smetterà di bloccare l&apos;evadibilità delle commesse: oggi ne blocca{" "}
            <b>{articolo?.n_ordini_impattati}</b>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className="text-xs text-muted-foreground" htmlFor="motivo-esclusione">
            Motivo (resta scritto accanto all&apos;articolo)
          </label>
          <input
            id="motivo-esclusione"
            type="text"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="es. imballaggio, certificato, materiale di consumo"
            className="h-9 w-full rounded-md border bg-card px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Annulla
          </Button>
          <Button size="sm" onClick={onConferma} disabled={pending}>
            {pending ? "Salvo…" : "Escludi"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
